import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { getStatAccent } from './statVisual';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface ChartCardProps {
  title: string;
  data: any[];
  type: 'bar' | 'line' | 'area' | 'pie';
  dataKey: string;
  nameKey: string;
  onMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  /** When set, numeric values (ticks, tooltips, pie labels) use at most this many fraction digits. */
  valueMaximumFractionDigits?: number;
  /** Render pie charts as a donut (hollow center). Only applies when type is "pie". */
  donut?: boolean;
}

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  data,
  type,
  dataKey,
  nameKey,
  onMenuClick,
  valueMaximumFractionDigits,
  donut = false,
}) => {
  const theme = useTheme();
  const { Icon, accent } = getStatAccent(theme, title);
  const chartPalette = [
    accent,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.info.main,
    theme.palette.warning.main,
  ];

  const formatChartNumber = (value: number): string => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return String(value);
    }
    if (valueMaximumFractionDigits !== undefined) {
      const s = value.toLocaleString(undefined, {
        maximumFractionDigits: valueMaximumFractionDigits,
        minimumFractionDigits: 0,
      });
      if (title.includes('TIME')) {
        return `${s}h`;
      }
      return s;
    }
    if (title.includes('TIME')) {
      return `${value.toFixed(1)}h`;
    }
    return value.toString();
  };

  const formatXAxis = (tickItem: string) => {
    if (title.includes('TIME')) {
      // If it's a date string, format it
      if (tickItem.includes('-')) {
        return new Date(tickItem).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    }
    return tickItem;
  };

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 20, right: 30, left: 20, bottom: 5 },
    };

    switch (type) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey={nameKey} 
                tickFormatter={formatXAxis}
                stroke={theme.palette.text.secondary}
              />
              <YAxis 
                stroke={theme.palette.text.secondary}
                tickFormatter={formatChartNumber}
              />
              <Tooltip 
                formatter={(value: number) => [formatChartNumber(value), title]}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                }}
              />
              <Legend />
              <Bar 
                dataKey={dataKey} 
                fill={accent}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey={nameKey} 
                tickFormatter={formatXAxis}
                stroke={theme.palette.text.secondary}
              />
              <YAxis 
                stroke={theme.palette.text.secondary}
                tickFormatter={formatChartNumber}
              />
              <Tooltip 
                formatter={(value: number) => [formatChartNumber(value), title]}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey={dataKey}
                stroke={accent}
                strokeWidth={2}
                dot={{ fill: accent, strokeWidth: 2 }}
                activeDot={{ r: 8, fill: accent }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey={nameKey} 
                tickFormatter={formatXAxis}
                stroke={theme.palette.text.secondary}
              />
              <YAxis 
                stroke={theme.palette.text.secondary}
                tickFormatter={formatChartNumber}
              />
              <Tooltip 
                formatter={(value: number) => [formatChartNumber(value), title]}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey={dataKey}
                fill={accent}
                stroke={accent}
                fillOpacity={0.28}
              />
            </AreaChart>
          </ResponsiveContainer>
        );
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                dataKey={dataKey}
                nameKey={nameKey}
                cx="50%"
                cy="50%"
                innerRadius={donut ? 60 : 0}
                outerRadius={100}
                paddingAngle={donut ? 2 : 0}
                fill={accent}
                label={({ name, value }) => `${name}: ${formatChartNumber(value)}`}
              >
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={chartPalette[index % chartPalette.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: number) => [formatChartNumber(value), title]}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '14px',
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'border-color 0.2s ease',
        '&:hover': {
          borderColor: alpha(theme.palette.text.primary, 0.18),
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
        <Icon sx={{ flexShrink: 0, fontSize: 24, color: accent }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: theme.palette.text.primary, flexGrow: 1 }}
        >
          {title}
        </Typography>
        <IconButton
          size="small"
          onClick={onMenuClick}
          sx={{ color: theme.palette.text.secondary }}
        >
          <MoreVertIcon />
        </IconButton>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 300 }}>
        {renderChart()}
      </Box>
    </Paper>
  );
};

export default ChartCard; 